import { Section } from "@/components/layout/section/Section";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@tanstack/react-router";
import {
  ChevronRight,
  Droplet,
  Droplets,
  Palette,
  Scissors,
  Sparkles,
  Wind,
  type LucideProps
} from "lucide-react";
import React from "react";

type IconComponent = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

interface CategoryTile {
  /** Display label for the tile. */
  title: string;
  /**
   * `categories.name` facet value(s) this tile links to. Multiple values are
   * OR-ed by the search page. Must match the taxonomy names exactly (see the
   * seed navigation in `api/apps/backend/src/scripts/seed/data/navigation.ts`).
   */
  categories: string[];
  /**
   * Approximate catalogue size, rendered as "{count}+ products". Curated by
   * hand as a truthful lower bound so it never overstates and doesn't drift as
   * the catalogue grows. Snapshot of the live dev index on 2026-07-22.
   */
  count: number;
  Icon: IconComponent;
  /** Tailwind classes for the icon chip (background + foreground). */
  color: string;
}

/**
 * Curated "Shop by category" grid. Intentionally frontend-owned and fully
 * static - no API call. Each tile navigates to the search page pre-filtered on
 * one or more `categories.name` facet values, so every tile lands on real
 * results. Only categories that currently have listings are shown (the catalogue
 * is hair-focused during the pilot); add tiles here as the catalogue broadens.
 */
const categoryTiles: CategoryTile[] = [
  {
    title: "Hair",
    categories: ["Hair"],
    count: 70,
    Icon: Scissors,
    color: "bg-rose-50 text-rose-700"
  },
  {
    title: "Shampoo",
    categories: ["Shampoo"],
    count: 60,
    Icon: Droplets,
    color: "bg-blue-50 text-blue-700"
  },
  {
    title: "Conditioner",
    categories: ["Conditioner"],
    count: 40,
    Icon: Droplet,
    color: "bg-cyan-50 text-cyan-700"
  },
  {
    title: "Hair Masks",
    categories: ["Hair Mask"],
    count: 30,
    Icon: Sparkles,
    color: "bg-purple-50 text-purple-700"
  },
  {
    title: "Hair Color",
    categories: ["Hair Color"],
    count: 10,
    Icon: Palette,
    color: "bg-amber-50 text-amber-700"
  },
  {
    title: "Styling",
    categories: ["Styling"],
    count: 5,
    Icon: Wind,
    color: "bg-green-50 text-green-700"
  }
];

const CategoryCard = React.memo(({ tile }: { tile: CategoryTile }) => {
  return (
    <Link
      to="/search"
      search={{ categories: tile.categories }}
      aria-label={`Shop ${tile.title}`}
      className="block"
    >
      <Card className="cursor-pointer transition-shadow hover:shadow-md">
        <CardContent className="p-4 text-center">
          <div
            className={`h-12 w-12 rounded-full ${tile.color} mx-auto mb-3 flex items-center justify-center`}
          >
            <tile.Icon className="h-6 w-6" />
          </div>
          <h3 className="mb-1 text-sm font-semibold">{tile.title}</h3>
          <p className="text-xs text-gray-500">{tile.count}+ products</p>
        </CardContent>
      </Card>
    </Link>
  );
});
CategoryCard.displayName = "CategoryCard";

function ShopByCategory() {
  return (
    <Section
      title="Shop by category"
      description="Browse our most popular product categories"
      rightContent={
        <Button variant="outline" asChild>
          <Link to="/search">
            See All Categories
            <ChevronRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {categoryTiles.map((tile) => (
          <CategoryCard key={tile.title} tile={tile} />
        ))}
      </div>
    </Section>
  );
}

export default React.memo(ShopByCategory);
